import {
  Button,
  Conditional,
  EclipseBadge,
  EmptyState,
  Select,
  Sidebar,
} from '@payaca/ambrosia';
import List from '@payaca/components/list/List';
import { getAddressAsString } from '@payaca/helpers/locationHelper';
import { clstx } from '@payaca/utilities/cvaMerge';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useReducer, useState, type JSX } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';

import useCreatePurchaseOrder from '@/api/mutations/purchaseOrders/useCreatePurchaseOrder';
import stockLocationKeys from '@/api/queries/stockLocations/keyFactory';
import useGetStockLocation from '@/api/queries/stockLocations/useGetStockLocation';
import { type MaterialState } from '@/ui/components/buildMaterialsListSidebar/BuildMaterialsListSidebar';
import CreateMaterialModal from '@/ui/components/createEditMaterialModal/CreateMaterialModal';
import InfiniteMaterialsList from '@/ui/components/infiniteMaterialsList/InfiniteMaterialsList';
import MaterialCard, {
  MaterialCardWithTransition,
} from '@/ui/components/materialCard/MaterialCard';
import MaterialTagFilter, {
  toMaterialTagOptions,
} from '@/ui/components/materialTagFilter/MaterialTagFilter';
import PurchaseOrderDetailFields from '@/ui/components/purchaseOrderDetailFields/PurchaseOrderDetailFields';
import SupplierSelectionField from '@/ui/components/supplierSelectionControl/SupplierSelectionField';

type Props = {
  stockLocationId: string;
  isOpen: boolean;
  onClose: () => void;
};

type TFormState = {
  supplierId?: number;
  tagIds?: number[];
  materials: MaterialState[];
  dueAt?: Date;
  additionalNotes?: string;
  showMaterialPrices: boolean;
  // The Address to deliver to, or blank when the order is collected rather than
  // delivered. Holds the Address's public ID.
  deliveryAddressId?: string;
};

const DEFAULT_FORM_STATE: TFormState = {
  materials: [],
  tagIds: [],
  showMaterialPrices: false,
};

// Mirrors the project create-PO sidebar: the "to order" panel is the constant
// middle column that slides from the right (while building the list) to the
// left (while entering order details).
type Stage = 'SUPPLIER' | 'MATERIALS' | 'DETAILS';
type StageState = { stage: Stage; transitionComplete: boolean };
type StageAction =
  | { type: 'GO_TO_SUPPLIER' }
  | { type: 'GO_TO_MATERIALS' }
  | { type: 'GO_TO_DETAILS' }
  | { type: 'TRANSITION_COMPLETE' }
  | { type: 'RESET' };

const DEFAULT_STAGE_STATE: StageState = {
  stage: 'SUPPLIER',
  transitionComplete: true,
};

const stageReducer = (state: StageState, action: StageAction): StageState => {
  switch (action.type) {
    case 'GO_TO_SUPPLIER':
      return { stage: 'SUPPLIER', transitionComplete: true };
    case 'GO_TO_MATERIALS':
      return { stage: 'MATERIALS', transitionComplete: false };
    case 'GO_TO_DETAILS':
      return { stage: 'DETAILS', transitionComplete: false };
    case 'TRANSITION_COMPLETE':
      return { ...state, transitionComplete: true };
    case 'RESET':
      return DEFAULT_STAGE_STATE;
    default:
      return state;
  }
};

/**
 * Raises a purchase order into a stock location. Modelled on the project create-PO
 * sidebar, diverging only where the domain forces it: there is no direct-supplier
 * journey, materials are picked from the catalogue rather than an existing list,
 * and delivery defaults to collection and can optionally be set to the location's
 * own address. The buyer picks a supplier,
 * composes the order from catalogue materials, then captures the order details;
 * the purchase order is raised in one call against the location's owned materials
 * list. Sending and confirming happen on the purchase order page afterwards.
 */
const RaiseStockPurchaseOrderSidebar: FC<Props> = (
  props: Props
): JSX.Element => {
  const { stockLocationId, isOpen, onClose } = props;

  const history = useHistory();
  const queryClient = useQueryClient();

  const { stockLocation } = useGetStockLocation(stockLocationId);
  const materialsListId = stockLocation?.materialsList.id;

  const [stageState, stageDispatch] = useReducer(
    stageReducer,
    DEFAULT_STAGE_STATE
  );
  const { stage, transitionComplete } = stageState;

  const [isCreatingMaterial, setIsCreatingMaterial] = useState(false);

  const createPurchaseOrder = useCreatePurchaseOrder();

  const formMethods = useForm<TFormState>({
    defaultValues: DEFAULT_FORM_STATE,
  });
  const { control, getValues, setValue, watch, handleSubmit, reset } =
    formMethods;

  const supplierId = watch('supplierId');
  const materials = watch('materials');
  const tagIds = watch('tagIds') ?? [];

  const hasOrderableMaterials =
    materials.length > 0 &&
    materials.every((material) => material.quantity > 0);

  const canRaise = !!materialsListId && !!supplierId && hasOrderableMaterials;

  const deliveryAddress = stockLocation?.address;
  const deliveryAddressSummary = deliveryAddress
    ? getAddressAsString({
        line1: deliveryAddress.line1 ?? undefined,
        line2: deliveryAddress.line2 ?? undefined,
        city: deliveryAddress.city ?? undefined,
        administrativeArea: deliveryAddress.administrativeArea ?? undefined,
        postcode: deliveryAddress.postalCode ?? undefined,
      })
    : '';

  // Tags present across the selected Materials. The filter is display-only: it
  // narrows what's shown, it does not drive what's ordered (Materials are added
  // from the catalogue).
  const availableTags = toMaterialTagOptions(
    materials.flatMap((material) => material.tags)
  );

  const visibleMaterials = tagIds.length
    ? materials.filter((material) =>
        material.tags.some((tag) => tagIds.includes(+tag.id))
      )
    : materials;

  const resetAll = () => {
    reset(DEFAULT_FORM_STATE);
    stageDispatch({ type: 'RESET' });
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleSupplierChange = (value?: number | null) => {
    const current = getValues('supplierId');
    setValue('supplierId', value ?? undefined);
    // Materials and the tag filter are scoped to the chosen Supplier, so clear
    // them when it actually changes.
    if (value !== current) {
      setValue('materials', []);
      setValue('tagIds', []);
    }
  };

  const addMaterial = (material: Omit<MaterialState, 'quantity'>) => {
    const current = getValues('materials');
    if (current.some((m) => m.id === material.id)) return;
    setValue('materials', [...current, { ...material, quantity: 1 }]);
  };

  const setMaterialQuantity = (materialId: string, quantity: number) => {
    setValue(
      'materials',
      getValues('materials').map((material) =>
        material.id === materialId ? { ...material, quantity } : material
      )
    );
  };

  const onSubmit = (state: TFormState) => {
    if (!canRaise || !materialsListId || !state.supplierId) return;

    createPurchaseOrder.mutate(
      {
        materialsListId,
        supplierId: state.supplierId.toString(),
        lines: state.materials.map((material) => ({
          material: {
            materialId: material.id,
            quantity: Math.round(material.quantity),
          },
        })),
        dueAt: state.dueAt ? state.dueAt.toISOString() : undefined,
        additionalNotes: state.additionalNotes?.trim() || undefined,
        showMaterialPrices: state.showMaterialPrices,
        deliveryAddressId: state.deliveryAddressId || undefined,
      },
      {
        onSuccess: (data) => {
          void queryClient.invalidateQueries({
            queryKey: stockLocationKeys.detail(stockLocationId),
          });
          resetAll();
          onClose();
          history.push(
            `/purchaseOrders/${data.createPurchaseOrder.id}?stockLocationId=${stockLocationId}`
          );
        },
      }
    );
  };

  const isReviewing = stage === 'DETAILS';

  const tagFilter = (
    <Controller
      control={control}
      name="tagIds"
      render={({ field: { value, onChange } }) => (
        <MaterialTagFilter
          availableTags={availableTags}
          value={value ?? []}
          onChange={onChange}
        />
      )}
    />
  );

  return (
    <FormProvider {...formMethods}>
      <Sidebar
        isOpen={isOpen}
        onClose={isCreatingMaterial ? undefined : handleClose}
        size={stage === 'SUPPLIER' ? 'standard' : 'lg'}
        title="Create purchase order"
        warnUserOnClose={materials.length > 0}
      >
        {stage === 'SUPPLIER' ? (
          <>
            <Sidebar.Body className="flex flex-col gap-2.5 overflow-auto bg-gray-50 p-3.5">
              <SupplierSelectionField
                selectedSupplierId={supplierId}
                onChange={handleSupplierChange}
                excludeSystemManaged
                enableSupplierCreation
              />
              <Conditional condition={!supplierId}>
                <EmptyState
                  className="mb-auto"
                  text="Start by selecting a supplier"
                  iconName="arrow-block-up.3"
                />
              </Conditional>
            </Sidebar.Body>
            <Sidebar.Footer className="flex justify-end">
              <Button
                disabled={!supplierId}
                onClick={() => stageDispatch({ type: 'GO_TO_MATERIALS' })}
              >
                Proceed
              </Button>
            </Sidebar.Footer>
          </>
        ) : (
          <Sidebar.Body className="relative overflow-hidden">
            <form
              className={clstx(
                // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Percentage size has no utility
                'absolute left-0 top-0 grid h-full w-[150%] grid-cols-3 transition-transform duration-500',
                isReviewing ? '-translate-x-1/3' : 'translate-x-0'
              )}
              onTransitionEnd={(e) => {
                if (e.propertyName === 'transform') {
                  stageDispatch({ type: 'TRANSITION_COMPLETE' });
                }
              }}
              onSubmit={handleSubmit(onSubmit)}
            >
              {/* Column 1 — catalogue (the build step's left pane) */}
              <div
                className={clstx(
                  'h-full overflow-auto',
                  isReviewing && transitionComplete && 'invisible'
                )}
              >
                {supplierId && (
                  <InfiniteMaterialsList
                    key={supplierId}
                    showPriceIncludingTax={false}
                    onAddMaterial={() => setIsCreatingMaterial(true)}
                    onMaterialClick={addMaterial}
                    disabledMaterials={materials.map((material) => material.id)}
                    defaultFilters={{ suppliers: [supplierId.toString()] }}
                  />
                )}
              </div>

              {/* Column 2 — the persistent "to order" panel */}
              <div
                className={clstx(
                  'flex h-full flex-col overflow-auto transition-colors duration-700',
                  isReviewing ? 'bg-white' : 'bg-gray-50'
                )}
              >
                <div className="sticky top-0 z-10 bg-inherit p-3.5">
                  <SupplierSelectionField
                    selectedSupplierId={supplierId}
                    onChange={() => undefined}
                    canChangeSupplier={false}
                  />

                  {tagFilter}

                  <Conditional condition={materials.length > 0}>
                    <div className="flex items-center justify-between">
                      <h4>Materials to include on order</h4>
                      <Button
                        variant="link"
                        onClick={() =>
                          stageDispatch({
                            type: isReviewing
                              ? 'GO_TO_MATERIALS'
                              : 'GO_TO_SUPPLIER',
                          })
                        }
                      >
                        {isReviewing ? 'Edit list' : 'Change supplier'}
                      </Button>
                    </div>
                  </Conditional>
                </div>

                <Conditional condition={materials.length === 0}>
                  <EmptyState
                    className="my-auto"
                    text="Add the materials to order into this location."
                  />
                </Conditional>

                <List
                  className="px-3.5"
                  items={visibleMaterials}
                  item={(material) =>
                    isReviewing ? (
                      <MaterialCard
                        name={material.name}
                        price={material.suppliedBy[0]?.price.unitPriceExcTax}
                        tags={material.tags}
                        thumbnailUrl={material.image?.thumbnailUrl || ''}
                        childrenClassName="w-full flex justify-between"
                      >
                        <EclipseBadge
                          badges={material.suppliedBy.map(
                            (i) => i.supplier.name
                          )}
                          variant="soft"
                          colour="gray"
                        />
                        <p>Qty: {material.quantity}</p>
                      </MaterialCard>
                    ) : (
                      <MaterialCardWithTransition
                        show={material.quantity > 0}
                        afterLeave={() =>
                          setValue(
                            'materials',
                            getValues('materials').filter((m) => m.quantity > 0)
                          )
                        }
                        name={material.name}
                        price={material.suppliedBy[0]?.price.unitPriceExcTax}
                        tags={material.tags}
                        thumbnailUrl={material.image?.thumbnailUrl || ''}
                        quantity={material.quantity}
                        quantityAriaLabel={`Quantity for ${material.name}`}
                        onChangeQuantity={(newQuantity) =>
                          setMaterialQuantity(material.id, newQuantity)
                        }
                      >
                        <EclipseBadge
                          badges={material.suppliedBy.map(
                            (i) => i.supplier.name
                          )}
                          variant="soft"
                          colour="gray"
                        />
                      </MaterialCardWithTransition>
                    )
                  }
                />

                <Conditional condition={!isReviewing}>
                  <div className="sticky bottom-0 mt-auto bg-gray-50 p-3.5 shadow-flipped-lg">
                    <Button
                      className="w-full"
                      disabled={!hasOrderableMaterials}
                      onClick={() => stageDispatch({ type: 'GO_TO_DETAILS' })}
                    >
                      Proceed
                    </Button>
                  </div>
                </Conditional>
              </div>

              {/* Column 3 — order details (the review step's right pane) */}
              <div
                className={clstx(
                  'flex h-full flex-col gap-4 overflow-auto border-l bg-gray-50 p-3.5',
                  !isReviewing && transitionComplete && 'invisible'
                )}
              >
                <h4>Order details</h4>

                <PurchaseOrderDetailFields
                  deliverTo={
                    <Controller
                      control={control}
                      name="deliveryAddressId"
                      render={({ field: { value, onChange } }) => (
                        <Select
                          multiple={false}
                          value={value ?? undefined}
                          options={
                            deliveryAddress
                              ? [
                                  {
                                    value: deliveryAddress.id,
                                    label: deliveryAddressSummary
                                      ? `${stockLocation?.name} - ${deliveryAddressSummary}`
                                      : (stockLocation?.name ?? ''),
                                  },
                                ]
                              : []
                          }
                          onChange={(selected) =>
                            onChange(selected || undefined)
                          }
                        />
                      )}
                    />
                  }
                />

                <Button
                  className="mt-auto"
                  type="submit"
                  disabled={!canRaise}
                  isProcessing={createPurchaseOrder.isPending}
                >
                  Create purchase order
                </Button>
              </div>
            </form>
          </Sidebar.Body>
        )}

        <CreateMaterialModal
          isOpen={isCreatingMaterial}
          onClose={() => setIsCreatingMaterial(false)}
          onPersistMaterialSuccess={(material) => {
            addMaterial(material);
            setIsCreatingMaterial(false);
          }}
        />
      </Sidebar>
    </FormProvider>
  );
};

export default RaiseStockPurchaseOrderSidebar;
